/**
 * Section Heroes slider overlay (data-layout="slider" only).
 * Uses Swiper slidesPerView 1.2. Styles are in bbb-upsell-slider.css.
 */
(() => {
  const SLIDES_PER_VIEW = 1.2;
  const SPACE_BETWEEN = 10;
  const SWIPE_THRESHOLD = 8;
  const patched = new WeakSet();

  const strings = (window.theme && window.theme.strings) || {};
  const prevLabel = strings.upsellSliderPrevious || 'Previous';
  const nextLabel = strings.upsellSliderNext || 'Next';

  const CHEVRON_PREV =
    '<svg width="11" height="14" viewBox="0 0 11 14" fill="none" aria-hidden="true"><path d="M8 2L3 7L8 12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const CHEVRON_NEXT =
    '<svg width="11" height="14" viewBox="0 0 11 14" fill="none" aria-hidden="true"><path d="M3 2L8 7L3 12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const sliderOptions = {
    slidesPerView: SLIDES_PER_VIEW,
    slidesPerGroup: 1,
    spaceBetween: SPACE_BETWEEN,
    autoHeight: false,
    allowTouchMove: false,
    simulateTouch: false,
    grabCursor: true,
    followFinger: true,
    shortSwipes: true,
    longSwipes: true,
    threshold: SWIPE_THRESHOLD,
    resistance: true,
    cssMode: false,
    passiveListeners: false,
    touchEventsTarget: 'container',
    preventClicks: true,
    preventClicksPropagation: true
  };

  const eventPoint = (event) => {
    const touch = (event.touches && event.touches[0]) || (event.changedTouches && event.changedTouches[0]);
    if (touch) return { x: touch.clientX, y: touch.clientY };
    return { x: event.clientX, y: event.clientY };
  };

  const isSliderHost = (el) => {
    const node = typeof el === 'string' ? document.querySelector(el) : el;
    if (!node || typeof node.closest !== 'function') return false;
    const host = node.closest('sh-upsell');
    return Boolean(host && host.getAttribute('data-layout') === 'slider');
  };

  const getSwiper = (host, slider) => (slider && slider.swiper) || (host && host.swiper) || null;

  const hosts = () => document.querySelectorAll('sh-upsell[data-layout="slider"]');

  const fillArrow = (button, svg) => {
    if (!button || button.querySelector('svg')) return;
    button.innerHTML = svg;
  };

  const injectControls = (host) => {
    const slider = host.querySelector('.sh-upsell-slider');
    if (!slider) return null;

    let footer = host.querySelector('.sh-upsell-slider-footer');
    if (!footer) {
      footer = document.createElement('div');
      footer.className = 'sh-upsell-slider-footer';
      slider.after(footer);
    }

    let nav = footer.querySelector('.sh-upsell-slider-nav');
    if (!nav) {
      nav = document.createElement('div');
      nav.className = 'sh-upsell-slider-nav';

      const prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'sh-upsell-slider-arrow sh-upsell-slider-prev';
      prev.setAttribute('aria-label', prevLabel);
      prev.innerHTML = CHEVRON_PREV;

      const next = document.createElement('button');
      next.type = 'button';
      next.className = 'sh-upsell-slider-arrow sh-upsell-slider-next';
      next.setAttribute('aria-label', nextLabel);
      next.innerHTML = CHEVRON_NEXT;

      nav.append(prev, next);
      footer.append(nav);
    }

    fillArrow(host.querySelector('.sh-upsell-slider-prev'), CHEVRON_PREV);
    fillArrow(host.querySelector('.sh-upsell-slider-next'), CHEVRON_NEXT);

    const swiper = getSwiper(host, slider);
    const paginations = Array.prototype.slice.call(host.querySelectorAll('.sh-upsell-slider-pagination'));
    let pagination =
      paginations.find((el) => el.childElementCount) ||
      paginations.find((el) => footer.contains(el)) ||
      paginations[0];

    if (pagination && pagination.parentElement !== footer && (pagination.childElementCount || swiper)) {
      footer.prepend(pagination);
    }

    paginations.forEach((el) => {
      if (el !== pagination) el.remove();
    });

    return {
      prevEl: host.querySelector('.sh-upsell-slider-prev'),
      nextEl: host.querySelector('.sh-upsell-slider-next'),
      paginationEl: host.querySelector('.sh-upsell-slider-pagination')
    };
  };

  const peekOptions = (el, options) => {
    if (!isSliderHost(el)) return options;
    const node = typeof el === 'string' ? document.querySelector(el) : el;
    const host = node && node.closest ? node.closest('sh-upsell') : null;
    if (host) injectControls(host);

    const next = Object.assign({}, options || {}, sliderOptions);
    const prevEl = host && host.querySelector('.sh-upsell-slider-prev');
    const nextEl = host && host.querySelector('.sh-upsell-slider-next');
    const paginationEl = host && host.querySelector('.sh-upsell-slider-pagination');
    if (prevEl && nextEl) {
      next.navigation = Object.assign({}, next.navigation || {}, { prevEl, nextEl });
    }
    if (paginationEl) {
      next.pagination = Object.assign({}, next.pagination || {}, { el: paginationEl });
    }
    return next;
  };

  const wrapSwiper = (SwiperClass) => {
    if (!SwiperClass || typeof SwiperClass !== 'function') return SwiperClass;
    if (patched.has(SwiperClass)) return SwiperClass;
    patched.add(SwiperClass);

    const Wrapped = new Proxy(SwiperClass, {
      construct(Target, args) {
        return Reflect.construct(Target, [args[0], peekOptions(args[0], args[1])], Target);
      }
    });

    patched.add(Wrapped);
    return Wrapped;
  };

  let current = wrapSwiper(window.Swiper);
  try {
    Object.defineProperty(window, 'Swiper', {
      configurable: true,
      enumerable: true,
      get() {
        return current;
      },
      set(value) {
        current = wrapSwiper(value);
      }
    });
  } catch (error) {
    window.Swiper = wrapSwiper(window.Swiper);
  }

  const bindModule = (swiper, params, name) => {
    if (!swiper || !swiper[name] || !params) return;
    Object.assign(swiper.params[name] || (swiper.params[name] = {}), params);
    const mod = swiper[name];
    if (typeof mod.destroy === 'function') mod.destroy();
    if (typeof mod.init === 'function') mod.init();
    if (typeof mod.render === 'function') mod.render();
    if (typeof mod.update === 'function') mod.update();
  };

  const applySwiper = (host) => {
    const slider = host.querySelector('.sh-upsell-slider');
    const nav = injectControls(host);
    const swiper = getSwiper(host, slider);
    if (!slider || !swiper || !swiper.params) return false;

    slider.querySelectorAll('.sh-upsell-slider-slide').forEach((slide) => {
      slide.style.removeProperty('width');
      slide.style.removeProperty('max-width');
      slide.style.removeProperty('margin-right');
    });

    Object.assign(swiper.params, sliderOptions);
    swiper.allowTouchMove = false;

    if (slider.dataset.bbbReady !== 'true') {
      if (nav && nav.prevEl && nav.nextEl) {
        bindModule(swiper, { prevEl: nav.prevEl, nextEl: nav.nextEl }, 'navigation');
      }
      if (nav && nav.paginationEl) {
        bindModule(swiper, { el: nav.paginationEl, clickable: true }, 'pagination');
      }
      slider.dataset.bbbReady = 'true';
    }

    if (typeof swiper.update === 'function') swiper.update();
    return true;
  };

  const bindSwipe = (host) => {
    const slider = host.querySelector('.sh-upsell-slider');
    if (!slider || slider.dataset.bbbSwipeBound === 'true') return;
    slider.dataset.bbbSwipeBound = 'true';

    const drag = {
      active: false,
      startX: 0,
      startY: 0,
      startTranslate: 0,
      axis: null,
      moved: false
    };

    const clamp = (swiper, value) => Math.max(swiper.maxTranslate(), Math.min(swiper.minTranslate(), value));

    const slideStep = (swiper) => {
      if (swiper.slidesSizesGrid && swiper.slidesSizesGrid[0]) {
        return swiper.slidesSizesGrid[0] + SPACE_BETWEEN;
      }
      return slider.clientWidth / SLIDES_PER_VIEW;
    };

    const onStart = (event) => {
      if (event.type === 'mousedown' && event.button !== 0) return;
      if (event.target.closest('.sh-upsell-slider-arrow, .sh-upsell-slider-pagination')) return;
      const swiper = getSwiper(host, slider);
      if (!swiper) return;
      const point = eventPoint(event);
      drag.active = true;
      drag.startX = point.x;
      drag.startY = point.y;
      drag.startTranslate = swiper.getTranslate();
      drag.axis = null;
      drag.moved = false;
    };

    const onMove = (event) => {
      if (!drag.active) return;
      const swiper = getSwiper(host, slider);
      if (!swiper) return;
      const point = eventPoint(event);
      const dx = point.x - drag.startX;
      const dy = point.y - drag.startY;
      if (!drag.axis) {
        if (Math.abs(dx) < SWIPE_THRESHOLD && Math.abs(dy) < SWIPE_THRESHOLD) return;
        drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (drag.axis !== 'x') return;
      if (event.cancelable) event.preventDefault();
      drag.moved = true;
      slider.classList.add('is-swiping');
      if (typeof swiper.setTransition === 'function') swiper.setTransition(0);
      swiper.setTranslate(clamp(swiper, drag.startTranslate + dx));
    };

    const onEnd = () => {
      if (!drag.active) return;
      const moved = drag.moved && drag.axis === 'x';
      drag.active = false;
      drag.axis = null;
      slider.classList.remove('is-swiping');
      const swiper = getSwiper(host, slider);
      if (swiper && moved && typeof swiper.slideToClosest === 'function') swiper.slideToClosest();
      window.setTimeout(() => {
        drag.moved = false;
      }, 0);
    };

    slider.addEventListener('touchstart', onStart, { passive: true });
    slider.addEventListener('touchmove', onMove, { passive: false });
    slider.addEventListener('touchend', onEnd);
    slider.addEventListener('touchcancel', onEnd);
    slider.addEventListener('mousedown', onStart);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
    slider.addEventListener(
      'click',
      (event) => {
        if (!drag.moved) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    let wheelAccum = 0;
    let wheelLocked = false;
    let unlockTimer = null;
    const onWheel = (event) => {
      const line = event.deltaMode === 1 ? 16 : 1;
      const horizontal = (event.shiftKey ? event.deltaY : event.deltaX) * line;
      const vertical = event.shiftKey ? 0 : event.deltaY * line;
      if (Math.abs(horizontal) <= Math.abs(vertical)) return;
      const swiper = getSwiper(host, slider);
      if (!swiper) return;
      if (event.cancelable) event.preventDefault();

      if (wheelLocked) return;
      if (Math.abs(horizontal) < 8 && Math.abs(wheelAccum) < 8) return;

      wheelAccum += horizontal;
      if (Math.abs(wheelAccum) < slideStep(swiper) * 0.28) return;

      const goingNext = wheelAccum > 0;
      wheelAccum = 0;
      wheelLocked = true;
      if (goingNext) swiper.slideNext();
      else swiper.slidePrev();

      window.clearTimeout(unlockTimer);
      unlockTimer = window.setTimeout(() => {
        wheelLocked = false;
        wheelAccum = 0;
      }, 360);
    };
    host.addEventListener('wheel', onWheel, { passive: false });
  };

  const enhanceHost = (host) => {
    if (!host || host.getAttribute('data-layout') !== 'slider') return false;
    injectControls(host);
    const ready = applySwiper(host);
    bindSwipe(host);
    return ready;
  };

  const enhanceAll = () => {
    let pending = 0;
    hosts().forEach((host) => {
      if (!enhanceHost(host)) pending += 1;
    });
    return pending;
  };

  let tries = 0;
  const retry = () => {
    const pending = enhanceAll();
    tries += 1;
    if (pending === 0 && hosts().length) return;
    if (tries > 40) return;
    window.setTimeout(retry, 250);
  };

  const start = () => {
    enhanceAll();
    retry();
  };

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('sh-upsell').then(start).catch(start);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.addEventListener('load', enhanceAll);
  document.addEventListener('shopify:section:load', enhanceAll);

  let resizeTimer;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      hosts().forEach((host) => {
        const slider = host.querySelector('.sh-upsell-slider');
        const swiper = getSwiper(host, slider);
        if (swiper && typeof swiper.update === 'function') swiper.update();
      });
    }, 150);
  });
})();
